import type { FilterCapabilityConfig } from '@omnea-app/packages/backend/filtering/types/baseFilterTypes'

// Filter/sort field definitions shared by the FE (column capabilities) and BE (Kysely config).
// Must not import Kysely/DB types — the frontend imports this.
const REQUESTS_FILTER_FIELDS = {
  filterableFields: {
    name: { type: 'scalar', column: 'requests.name', scalarType: 'string' },
    publicId: { type: 'scalar', column: 'requests.public_id', scalarType: 'string' },
    createdAt: { type: 'scalar', column: 'requests.created_at', scalarType: 'timestamp' },
    completedAt: { type: 'scalar', column: 'requests.completed_at', scalarType: 'timestamp' },
    priority: { type: 'scalar', column: 'requests.priority', scalarType: 'enum' },
    // enumMapping bridges the FE's status options to the generated `requests.status` column,
    // which stores `inProgress` where the UI (and `deriveRequestStatus`) show `pending`.
    status: {
      type: 'scalar',
      column: 'requests.status',
      scalarType: 'enum',
      enumMapping: { pending: 'inProgress', completed: 'completed', rejected: 'rejected', paused: 'paused' },
    },
    // Relation fields carry only `type` (all the FE needs); their Kysely DB defs live in
    // buildRequestsKyselyFilter (RELATION_DEFS).
    supplier: { type: 'relation' },
    workflowTitle: { type: 'relation' },
    tags: { type: 'relation' },
    // Core-data relations resolved off request_computed_properties FKs; DB defs (COMPUTED join columns)
    // live in buildRequestsKyselyFilter (RELATION_DEFS). department and costCenter both point at
    // `departments`; market points at custom_core_data_options.value.
    department: { type: 'relation' },
    costCenter: { type: 'relation' },
    market: { type: 'relation' },
    buyerLegalEntity: { type: 'relation' },
    paymentTerm: { type: 'relation' },
    paymentMethod: { type: 'relation' },
    // Computed fields: Kysely handlers live in buildRequestsKyselyFilter (COMPUTED_DEFS). `computedKind:
    // 'person'` fields match a user's full name (contains/equals + negations); `computedKind: 'category'`
    // coalesces its two source tables (contains + doesNotContain). allCurrentAssignees, though multi-valued,
    // also sorts/groups by a normalized set-key (buildRequestsKyselySorting).
    createdBy: { type: 'computed', computedKind: 'person' },
    allCurrentAssignees: { type: 'computed', computedKind: 'person' },
    businessOwner: { type: 'computed', computedKind: 'person' },
    budgetHolder: { type: 'computed', computedKind: 'person' },
    projectOwner: { type: 'computed', computedKind: 'person' },
    category: { type: 'computed', computedKind: 'category' },
    // Resolved from requests.tag_ids against the workflow's condition groups; substring-matches a group
    // name (any precedence). EXISTS-based, so it also serves the NULL-safe `doesNotContain`. Filter-only:
    // no stable column to sort by.
    autoGeneratedTags: { type: 'computed', computedKind: 'autoGeneratedTags' },
    // request_computed_properties columns, filtered as plain scalars via the 1:1 join in listRequestsQuery.
    // Keyed by the FE grid column field name, not the column.
    goLiveDate: { type: 'scalar', column: 'request_computed_properties.go_live_date', scalarType: 'date' },
    cancellationNotice: { type: 'scalar', column: 'request_computed_properties.cancellation_notice_days', scalarType: 'number' },
    note: { type: 'scalar', column: 'request_computed_properties.note', scalarType: 'string' },
    type: { type: 'scalar', column: 'request_computed_properties.type', scalarType: 'string' },
    stage: { type: 'scalar', column: 'request_computed_properties.stage', scalarType: 'string' },
    contractStartDate: { type: 'scalar', column: 'request_computed_properties.contract_start_date', scalarType: 'date' },
    contractEndDate: { type: 'scalar', column: 'request_computed_properties.contract_end_date', scalarType: 'date' },
    // value/annualValue/negotiatedSavings are stored in base currency, but the backend filters and sorts
    // them by the displayed default-currency value — buildRequestValueFilterDefs and the sort mappings
    // override these column defs, so the `column` here is not what money queries run against.
    value: { type: 'scalar', column: 'request_computed_properties.value_number', scalarType: 'number' },
    annualValue: {
      type: 'scalar',
      column: 'request_computed_properties.annual_value_number',
      scalarType: 'number',
    },
    // Only negotiatedSavings has a denormalized annualized column; costAvoidance/financeBudget do not,
    // so they display but must not be added here — there is no column to sort/filter them by.
    negotiatedSavings: {
      type: 'scalar',
      column: 'request_computed_properties.annual_negotiated_savings',
      scalarType: 'number',
    },
    // Display-resolved scalars: the grid value is composed from several source tables, not one cp column, so
    // these carry no `column` — the backend supplies a resolving expression (RESOLVED_SCALAR_FILTER_DEFS in
    // resolvedRequestFieldExpressions). renewalDate/cancelBy resolve from the renewals record + agreement
    // term, not the form-derived cp columns.
    purchaseOrderNumber: { type: 'scalar', scalarType: 'string' },
    renewalDate: { type: 'scalar', scalarType: 'date' },
    cancelBy: { type: 'scalar', scalarType: 'date' },
  },
  sortableFields: {
    name: true,
    publicId: true,
    createdAt: true,
    completedAt: true,
    supplier: true,
    workflowTitle: true,
    createdBy: true,
    projectOwner: true,
    status: true,
    goLiveDate: true,
    cancellationNotice: true,
    note: true,
    // priority is a direct requests.priority enum; sorted via a CASE mirroring the client comparator
    // (buildRequestsKyselySorting).
    priority: true,
    // Computed-property scalars sort by their cp/requests column; relations and computed-name fields
    // sort by a correlated subquery (buildRequestsKyselySorting FIELD_MAPPINGS).
    type: true,
    stage: true,
    contractStartDate: true,
    contractEndDate: true,
    value: true,
    annualValue: true,
    negotiatedSavings: true,
    // Display-resolved: sorted by the resolving expression in buildRequestsKyselySorting FIELD_MAPPINGS.
    purchaseOrderNumber: true,
    renewalDate: true,
    cancelBy: true,
    department: true,
    costCenter: true,
    market: true,
    buyerLegalEntity: true,
    paymentTerm: true,
    paymentMethod: true,
    businessOwner: true,
    budgetHolder: true,
    category: true,
    // Multi-valued relations sorted by a normalized set-key (buildRequestsKyselySorting FIELD_MAPPINGS).
    tags: true,
    allCurrentAssignees: true,
  },
  // Fields the backend can group by server-side — must mirror the `groupBy` enum (`requestGroupableFields`
  // in listRequestsPaginatedV2Schema). The grid offers grouping only on these; a grouped query outside the
  // set falls back to the client path.
  groupableFields: {
    workflowTitle: true,
    supplier: true,
    department: true,
    market: true,
    buyerLegalEntity: true,
    costCenter: true,
    category: true,
    name: true,
    type: true,
    stage: true,
    projectOwner: true,
    createdBy: true,
    priority: true,
    status: true,
    // Grouped by the same set-key they sort by: one group per distinct tag/assignee set.
    tags: true,
    allCurrentAssignees: true,
  },
} as const

// Widened for the frontend's column capabilities.
export const REQUESTS_FILTER_CAPABILITIES: FilterCapabilityConfig = REQUESTS_FILTER_FIELDS
// Literal types preserved for the backend Kysely config.
export const REQUESTS_FILTER_FIELDS_DEF = REQUESTS_FILTER_FIELDS

export type RequestSortableField = keyof (typeof REQUESTS_FILTER_FIELDS_DEF)['sortableFields']

export type RequestGroupableField = keyof (typeof REQUESTS_FILTER_FIELDS_DEF)['groupableFields']

export const isRequestGroupableField = (field: string): field is RequestGroupableField =>
  field in REQUESTS_FILTER_FIELDS_DEF.groupableFields

type RequestFilterableFields = (typeof REQUESTS_FILTER_FIELDS_DEF)['filterableFields']

type RequestFieldOfType<T extends RequestFilterableFields[keyof RequestFilterableFields]['type']> = {
  [K in keyof RequestFilterableFields]: RequestFilterableFields[K] extends { type: T } ? K : never
}[keyof RequestFilterableFields]

// The backend must supply a KyselyRelationDef for exactly the `type: 'relation'` keys.
export type RequestRelationField = RequestFieldOfType<'relation'>
// The backend must supply a handler for exactly the `type: 'computed'` keys.
export type RequestComputedField = RequestFieldOfType<'computed'>
